import { useState, useEffect, useMemo, useCallback } from "react";
import { getImages, getMetrics } from "../helpers/utils";

/**
 * Computes the overall status based on build and runtime states
 */
const computeOverallStatus = (buildStatus, buildReason, runtimeStatus, pods) => {
  // Building takes precedence
  if (buildReason === "BuildRunning" || buildReason === "BuilderNotReady") {
    return "building";
  }

  // Then deploying
  if (runtimeStatus === "Unknown") {
    return "deploying";
  }

  // Check for errors
  if (buildStatus === "False") {
    return "error";
  }

  // Check runtime errors
  if (runtimeStatus === "error") {
    return "error";
  }

  // Live if ready and has pods
  if (buildStatus === "True" && pods > 0) {
    return "live";
  }

  // Idle if built but no pods (scaled to zero)
  return "idle";
};

/**
 * Merges build data (from getImages) with runtime data (from getMetrics)
 */
const mergeAppData = (buildData, runtimeData) => {
  // Create maps of runtime data for quick lookup
  const runtimeByName = new Map();
  const runtimeByImageUid = new Map();

  runtimeData.forEach((item) => {
    const name = item?.service?.metadata?.name;
    const imageUid = item?.service?.metadata?.labels?.imageUid;

    if (name) {
      runtimeByName.set(name, item);
      // Also map with -image suffix for matching builds
      runtimeByName.set(name + "-image", item);
    }
    if (imageUid) {
      runtimeByImageUid.set(imageUid, item);
    }
  });

  // Merge build data with runtime data
  return buildData.map((buildItem) => {
    const name = buildItem?.metadata?.name;
    const displayName = buildItem?.metadata?.displayName;
    const uid = buildItem?.metadata?.uid;

    // Try multiple matching strategies:
    // 1. Match by build UID to runtime imageUid label
    // 2. Match by displayName (e.g., "lead-image" → "lead")
    // 3. Match by displayName without -image suffix
    // 4. Exact name match
    const runtimeItem = runtimeByImageUid.get(uid) ||
                        runtimeByName.get(displayName) ||
                        runtimeByName.get(displayName?.replace(/-image$/, "")) ||
                        runtimeByName.get(name) ||
                        runtimeByName.get(name?.replace(/-image$/, ""));

    const buildCondition = buildItem?.status?.conditions?.[0];
    const runtimeConditions = runtimeItem?.service?.status?.conditions;
    const runtimeCondition = runtimeConditions?.[0];

    // Determine runtime status
    const runtimeUrl = runtimeItem?.service?.status?.url || runtimeItem?.url || runtimeItem?.service?.url;
    let runtimeStatus = "stopped";
    if (runtimeCondition?.status === "Unknown") {
      runtimeStatus = "deploying";
    } else if (runtimeConditions?.some(c => c.status !== "True")) {
      runtimeStatus = "error";
    } else if (runtimeItem?.pods > 0) {
      runtimeStatus = "running";
    } else if (runtimeUrl) {
      // Service exists with URL but no pods = scaled to zero (Knative idle state)
      runtimeStatus = "scaled";
    }

    const overallStatus = computeOverallStatus(
      buildCondition?.status,
      buildCondition?.reason,
      runtimeStatus,
      runtimeItem?.pods || 0
    );

    // Coordinator returns metadata.displayName with the kpack
    // "-image" suffix preserved ("my-react-app-image"). That's the
    // resource name leaking onto user-facing surfaces. Strip the
    // suffix here so every card / modal / chip downstream gets the
    // friendly name with zero further effort.
    const stripImage = (s) => (s ? s.replace(/-image$/, "") : s);
    // Resolve build state with transient/initializing cases handled.
    // Without this, fresh Images with no conditions yet OR kpack's
    // brief "SourceResolver is not ready" race window get classified
    // as "failed" and the card flashes red right after Create — the
    // worst possible first impression. Map to "pending" instead.
    const buildReason = buildCondition?.reason || "";
    const buildStatus = (() => {
      if (!buildCondition) return "pending";
      if (buildReason === "BuildRunning" || buildReason === "BuilderNotReady") {
        return "building";
      }
      if (buildCondition.status === "True") return "ready";
      if (buildCondition.status === "Unknown") return "pending";
      // status === "False" — distinguish real failures from kpack's
      // transient init messages.
      const msg = (buildCondition.message || "").toLowerCase();
      if (msg.includes("sourceresolver") && msg.includes("not ready")) return "pending";
      if (buildReason.toLowerCase().includes("notready")) return "pending";
      return "failed";
    })();

    return {
      id: buildItem?.metadata?.uid,
      name: name,
      displayName: stripImage(buildItem?.metadata?.displayName) || stripImage(name),

      build: {
        status: buildStatus,
        reason: buildCondition?.reason,
        message: buildCondition?.message,
        latestImage: buildItem?.status?.latestImage,
        gitUrl: buildItem?.spec?.source?.git?.url,
        buildCounter: buildItem?.status?.buildCounter,
        latestBuildReason: buildItem?.status?.latestBuildReason,
        resourceVersion: buildItem?.metadata?.resourceVersion,
        createdOn: buildItem?.metadata?.creationTimestamp
          ? new Date(buildItem.metadata.creationTimestamp)
          : null,
      },

      runtime: {
        status: runtimeStatus,
        url: runtimeUrl,
        pods: runtimeItem?.pods || 0,
        cpu: runtimeItem?.cpu || 0,
        memory: runtimeItem?.memory || 0,
        conditions: runtimeConditions,
        serviceName: runtimeItem?.service?.metadata?.name,
      },

      overallStatus,
      hasActivity: overallStatus === "building" || overallStatus === "deploying",
      lastActivity: buildItem?.metadata?.creationTimestamp
        ? new Date(buildItem.metadata.creationTimestamp)
        : new Date(),

      // Keep raw data for modals
      _raw: {
        build: buildItem,
        runtime: runtimeItem,
      },
    };
  });
};

/**
 * Hook for fetching and merging build and runtime data
 */
export function useUnifiedApps({
  showError,
  buildingRefreshInterval = 3000,
  normalRefreshInterval = 10000,
}) {
  const [buildData, setBuildData] = useState([]);
  const [runtimeData, setRuntimeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch both data sources
  const fetchData = useCallback(async (showLoadingState = false) => {
    if (showLoadingState) {
      setLoading(true);
    }

    try {
      // Fetch both in parallel
      const [buildResult, runtimeResult] = await Promise.all([
        new Promise((resolve, reject) => {
          getImages(null, (result, err) => {
            if (err) reject(err);
            else resolve(result || []);
          });
        }),
        new Promise((resolve, reject) => {
          getMetrics(null, (result, err) => {
            if (err) reject(err);
            else resolve(result || []);
          });
        }),
      ]);

      setBuildData(buildResult);
      setRuntimeData(runtimeResult);
      setError(null);
    } catch (err) {
      setError(err);
      showError?.(err);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  // Memoized merged data
  const apps = useMemo(() => {
    return mergeAppData(buildData, runtimeData);
  }, [buildData, runtimeData]);

  // Detect if any app is building/deploying for faster polling
  const hasActiveApps = useMemo(() => {
    return apps.some(app => app.hasActivity);
  }, [apps]);

  // Initial fetch
  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  // Adaptive polling
  useEffect(() => {
    const interval = hasActiveApps ? buildingRefreshInterval : normalRefreshInterval;

    const timer = setInterval(() => {
      fetchData(false);
    }, interval);

    return () => clearInterval(timer);
  }, [hasActiveApps, buildingRefreshInterval, normalRefreshInterval, fetchData]);

  // Manual refresh
  const refresh = useCallback(() => {
    fetchData(false);
  }, [fetchData]);

  return {
    apps,
    loading,
    error,
    refresh,
    hasActiveApps,
  };
}

export default useUnifiedApps;
